import React, { useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import cn from '@/utils/cn';
import apiErrorMessage from '../../shared/apiErrors';
import { Field, panelClass } from '../../shared/formPanel';
import SPAButton from '../../shared/SPAButton';
import SPACheckbox from '../../shared/SPACheckbox';
import SPAInput from '../../shared/SPAInput';
import SPASectionHeading from '../../shared/SPASectionHeading';
import { useSPAToast } from '../../shared/SPAToast';
import {
  EditableUnit, LibraryVideo, useRemoveUnitVideo, useSetUnitVideo, useVideoSearch,
} from './useUnit';

const posterClass = cn(
  'tw-flex tw-h-16 tw-w-28 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md',
  'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-tertiary',
);

const Poster = ({ alt, src }: { alt: string; src: string | null }) => (
  <span className={posterClass}>
    {src
      ? <img alt={alt} className="tw-h-full tw-w-full tw-object-cover" src={src} />
      : <span aria-hidden="true" className="tw-text-xs tw-text-spa-tertiary">no poster</span>}
  </span>
);

const resultClass = cn(
  'tw-flex tw-items-center tw-gap-3 tw-py-2',
  'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary first:tw-border-t-0',
);

// The legacy VideoManager, as a section: the unit's one video from the video library, with a
// file-name search to pick or replace it. Uploading to the library stays on the Videos page.
const UnitVideoPanel = () => {
  const unit = useOutletContext<EditableUnit>();
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const spaToast = useSPAToast();
  const setVideo = useSetUnitVideo(pid, unit.id);
  const removeVideo = useRemoveUnitVideo(pid, unit.id);
  const [query, setQuery] = useState('');
  const [autoplay, setAutoplay] = useState(unit.model_video?.autoplay ?? false);
  const results = useVideoSearch(query);
  const current = unit.model_video;

  const choose = (video: LibraryVideo) => setVideo.mutate({ autoplay, videoId: video.id }, {
    onSuccess: () => {
      setQuery('');
      spaToast.success(`Set “${video.video_file_name}” as the unit video.`);
    },
  });

  const remove = () => {
    if (!current) return;
    removeVideo.mutate(current.id, { onSuccess: () => spaToast.success('Removed the unit video.') });
  };

  const error = setVideo.error ?? removeVideo.error;

  return (
    <div className={panelClass}>
      <SPASectionHeading className="tw-mb-3" title="Unit Video" />

      {current ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
          <Poster alt="" src={current.poster_url} />
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{current.video_file_name}</p>
            <p className="tw-m-0 tw-text-xs tw-text-spa-tertiary">
              {current.autoplay ? 'Auto-plays on the unit page.' : 'Plays when started.'}
            </p>
          </div>
          <SPAButton disabled={removeVideo.isLoading} size="xs" variant="danger-outline" onClick={remove}>
            Remove video
          </SPAButton>
        </div>
      ) : (
        <p className="tw-m-0 tw-text-sm tw-italic tw-text-spa-tertiary">No video on this unit yet.</p>
      )}

      <hr className="tw-my-5 tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary" />

      <SPASectionHeading className="tw-mb-3" title={current ? 'Replace the video' : 'Choose a video'} />
      <div className="tw-flex tw-flex-col tw-gap-3 md:tw-max-w-lg">
        <Field
          hint="Searches the video library by file name. Upload new videos on the Videos page."
          htmlFor="unit_video_search"
          label="Find a video"
        >
          <SPAInput
            id="unit_video_search"
            placeholder="Type part of a file name…"
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </Field>
        <SPACheckbox checked={autoplay} onChange={event => setAutoplay(event.target.checked)}>
          Enable auto-play
        </SPACheckbox>
      </div>

      {results.isFetching && <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-tertiary">Searching…</p>}
      {results.data && results.data.length === 0 && (
        <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-tertiary">No videos match “{query.trim()}”.</p>
      )}
      {results.data && results.data.length > 0 && (
        <ul aria-label="Matching videos" className="tw-m-0 tw-mt-3 tw-list-none tw-p-0">
          {results.data.map(video => (
            <li key={video.id} className={resultClass}>
              <Poster alt="" src={video.poster_url} />
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-spa-primary">
                {video.video_file_name}
              </span>
              <SPAButton disabled={setVideo.isLoading} size="xs" variant="primary" onClick={() => choose(video)}>
                Use this video
              </SPAButton>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-error" role="alert">
          {apiErrorMessage(error, 'Couldn’t update the unit video. Try again.')}
        </p>
      )}
    </div>
  );
};

export default UnitVideoPanel;
